import React, {Fragment, useEffect, useRef} from 'react';
import {Button, FormControl} from 'react-bootstrap';
import {useSelector} from 'react-redux';

import {ConfirmationDialog} from "../../../Common/ConfirmationDialog";
import {Glyphicon} from "../../../Common/GlyphIcon";
import {SB_ENTER_KEY_CODE, SB_ESC_KEY_CODE} from "../../../../Constants";
import {useQueryFavoritesController} from "../../../../Hooks/QueryToolFavoritesHooks";
import {useHasColumns} from "../../../../Hooks/QueryToolDataHooks";
import {useQueryParams} from "../../../../../../monitoring/src/Hooks/RouteHooks";
import {KeyboardUtils} from "../../../../../../monitoring/src/Utils/KeyboardUtils";
import {selectQueryDefinition} from "../../../../Redux/Selectors/queryToolSelectors";

export const QueryToolTableTitle = () => {
    const definition = useSelector(selectQueryDefinition);
    const {savedQueryId} = useQueryParams();

    const favoritesController = useQueryFavoritesController({definition, savedQueryId});

    return (
        <Fragment>
            <FavoritesDialogs controller={favoritesController} />
            <div className="sb-query-table-header">
                <h2>Query</h2>
                <FavoritesToolbar controller={favoritesController} />
            </div>
        </Fragment>
    );
};

const FavoritesDialogs = ({controller}) => {
    const favoriteInput = useRef(null);

    const {
        favoriteName,
        setFavoriteName,
        showFavoriteModal,
        addFavorite,
        closeFavoriteModal,
        changedFavoriteId,
        confirmFavoriteChange,
        cancelFavoriteChange,
        showNewQuery,
        confirmNewQuery,
        closeNewQueryDialog,
        showDelete,
        confirmDeleteFavorite,
        closeDeleteDialog,
    } = controller;

    useEffect(() => {
        if (showFavoriteModal) {
            favoriteInput.current?.focus?.();
        }
    }, [showFavoriteModal]);

    return (
    <Fragment>
        <ConfirmationDialog
            Body={
                <div>
                    <label>Query Name</label>
                    <FormControl
                        ref={favoriteInput}
                        type="text"
                        value={favoriteName}
                        onChange={(e) => setFavoriteName(e.target.value)}
                        onKeyDown={(e) => {
                            if (favoriteName) {
                                KeyboardUtils.handleKeyPress(SB_ENTER_KEY_CODE, e, addFavorite);
                            }
                            KeyboardUtils.handleKeyPress(SB_ESC_KEY_CODE, e, closeFavoriteModal);
                        }}
                    />
                </div>
            }
            Display={showFavoriteModal}
            OnConfirm={addFavorite}
            OnHide={closeFavoriteModal}
            ConfirmationButtonMessage={"Save Favorite"}
            CancelButtonMessage={"Cancel"}
            DisableConfirmationButton={!favoriteName}
        />
        <ConfirmationDialog
            Title="Discard Changes?"
            Body="You have unsaved changes, are you sure you want to discard changes and load another favorite?"
            Display={changedFavoriteId}
            OnConfirm={confirmFavoriteChange}
            OnHide={cancelFavoriteChange}
            CancelButtonMessage="Cancel"
            ConfirmationButtonMessage="Discard Changes"
        />
        <ConfirmationDialog
            Title="Discard Changes?"
            Body="You have unsaved changes, are you sure you want to discard changes and create a new query?"
            Display={showNewQuery}
            OnConfirm={confirmNewQuery}
            OnHide={closeNewQueryDialog}
            CancelButtonMessage="Cancel"
            ConfirmationButtonMessage="Discard Changes"
        />
        <ConfirmationDialog
            Title="Delete Favorite?"
            Body="Are you sure you want to permanently delete this favorite?"
            Display={showDelete}
            OnDelete={confirmDeleteFavorite}
            OnHide={closeDeleteDialog}
            CancelButtonMessage="Cancel"
        />
    </Fragment>
    );
};

const FavoritesToolbar = ({controller}) => {
    const hasColumns = useHasColumns();

    const {
        currentFavoriteId,
        savedQueries,
        changed,
        isShared,
        handleFavoriteSelect,
        updateFavorite,
        openFavoriteModal,
        openDeleteDialog,
        hasUnsavedChanges,
        openNewQueryDialog,
        confirmNewQuery,
    } = controller;

    const handleNewQueryClick = () => {
        if (hasUnsavedChanges()) {
            openNewQueryDialog();
        } else {
            confirmNewQuery();
        }
    };

    return (
    <div className="sb-query-favorites">
        {hasColumns && (
            <Button variant="default" onClick={handleNewQueryClick}>
                <Glyphicon glyph="file" />
                New Query
            </Button>
        )}
        <FormControl
            as="select"
            value={currentFavoriteId}
            onChange={(e) => handleFavoriteSelect(e.target.value)}
        >
            <option value={''}>Favorites</option>
            {savedQueries.map(x => (
                <option key={x.savedQueryId} value={x.savedQueryId}>
                    {x.name}{x.isShared ? ' ⭐️' : ''}
                </option>
            ))}
        </FormControl>
        {currentFavoriteId && changed && !isShared && (
            <Button variant="default" onClick={updateFavorite}>
                <Glyphicon glyph="save" />
                Save Favorite
            </Button>
        )}
        {currentFavoriteId && (
            <Button variant="default" onClick={openFavoriteModal}>
                <Glyphicon glyph="save" />
                Save as New Favorite
            </Button>
        )}
        {currentFavoriteId && !isShared && (
            <Button variant="default" onClick={openDeleteDialog}>
                <Glyphicon glyph="trash" />
                Delete Favorite
            </Button>
        )}
        {hasColumns && !currentFavoriteId && (
            <Button variant="default" onClick={openFavoriteModal}>
                <Glyphicon glyph="plus" />
                Add to Favorites
            </Button>
        )}
    </div>
    );
};
